import { Timestamp } from "firebase/firestore";

// Types matching AcademicsManagement and GuardianFinance
// Types matching AcademicsManagement and GuardianFinance
export interface Term {
  id: string;
  number: number;
  title: string;
  openingDate: Date | Timestamp | undefined;
  closingDate: Date | Timestamp | undefined;
  termKey?: string;
  year?: number;
}

export type TermInfo = Term;

export interface TermTotals {
  term1: number;
  term2: number;
  term3: number;
  annual: number;
}

export interface PaymentAllocations {
  term1: number;
  term2: number;
  term3: number;
}

/**
 * Normalizes a Firestore Timestamp or Date object to a native JS Date
 */
export const normalizeDate = (date: Date | Timestamp | string | undefined): Date | undefined => {
  if (!date) return undefined;
  if (date instanceof Timestamp) return date.toDate();
  if (typeof date === 'string') return new Date(date);
  return date as Date;
};

/**
 * Determines the current term based on a given date and the school's term schedule.
 * If the date falls between terms or outside defined ranges, it uses standard defaults
 * or logic to determine the "upcoming" or "active" context.
 */
export const getCurrentTerm = (terms: Term[], date: Date = new Date()): Term | null => {
  if (!terms || terms.length === 0) return null;

  const normalizedTerms = terms.map(t => ({
    ...t,
    start: normalizeDate(t.openingDate),
    end: normalizeDate(t.closingDate)
  })).filter(t => t.start && t.end);

  // 1. Check if date is strictly WITHIN a term
  const activeTerm = normalizedTerms.find(
    t => date >= t.start! && date <= t.end!
  );
  if (activeTerm) return activeTerm as unknown as Term;

  // 2. If not in a term, find the NEXT upcoming term (for holidays)
  // Sort terms by start date
  normalizedTerms.sort((a, b) => a.start!.getTime() - b.start!.getTime());
  
  const upcomingTerm = normalizedTerms.find(t => t.start! > date);
  if (upcomingTerm) return upcomingTerm as unknown as Term;

  // 3. If past all terms (e.g. December holiday), default to Term 1 of next year or just null
  // For simplicity/safety, we often default to Term 3 or Term 1 depending on the month
  const month = date.getMonth(); // 0-11
  if (month >= 11) { // December
    return terms.find(t => t.number === 1) || null; // Preparing for Term 1
  }

  // Fallback: Return the last term if we are past everything for the year
  return normalizedTerms[normalizedTerms.length - 1] as unknown as Term;
};

/**
 * Helper to determine simple term label (e.g., 'term1') from a date, 
 * useful when Term objects aren't fully loaded yet.
 */
export const getTermKeyFromDate = (date: Date): 'term1' | 'term2' | 'term3' => {
  const month = date.getMonth();
  if (month <= 3) return 'term1'; // Jan - April
  if (month <= 7) return 'term2'; // May - Aug
  return 'term3'; // Sept - Dec
};

/**
 * Calculates the payment status label and color
 */
export const getPaymentStatus = (paid: number, required: number) => {
  if (required <= 0) return { label: 'N/A', color: 'bg-gray-100 text-gray-600', status: 'na' };
  if (paid >= required) return { label: 'Fully Paid', color: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300', status: 'paid' };
  if (paid > 0) return { label: 'Partial', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300', status: 'partial' };
  return { label: 'Unpaid', color: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300', status: 'unpaid' };
};

/**
 * Core Fee Math: Calculates balances, carryovers, and net due for all terms.
 * This is the "Source of Truth" for how money flows between terms.
 */
export const calculateFeeBalances = (totals: TermTotals, paid: PaymentAllocations, broughtForward: number = 0, currentTerm?: 'term1' | 'term2' | 'term3') => {
  // Term 1
  const term1Required = totals.term1 || 0;
  const term1Paid = paid.term1 || 0;
  // Add historical debt (broughtForward) to Term 1
  const term1TotalDue = term1Required + broughtForward; 
  // Allow negative balance (credit) to flow
  const term1NetBalance = term1TotalDue - term1Paid; 

  // Term 2
  const term2Required = totals.term2 || 0;
  // Carryover: Full balance from Term 1 (Debt > 0, Credit < 0)
  const term2Carryover = term1NetBalance; 
  const term2TotalDue = term2Required + term2Carryover;
  const term2Paid = paid.term2 || 0;
  const term2NetBalance = term2TotalDue - term2Paid;

  // Term 3
  const term3Required = totals.term3 || 0;
  const term3Carryover = term2NetBalance;
  const term3TotalDue = term3Required + term3Carryover;
  const term3Paid = paid.term3 || 0;
  const term3NetBalance = term3TotalDue - term3Paid;

  // Calculate Total Outstanding based on Current Term Context
  // If currentTerm is provided, we only consider debts up to that term.
  let totalOutstanding = term3NetBalance; // Default to full year if not specified

  if (currentTerm === 'term1') {
      totalOutstanding = term1NetBalance;
  } else if (currentTerm === 'term2') {
      totalOutstanding = term2NetBalance;
  } else if (currentTerm === 'term3') {
      totalOutstanding = term3NetBalance;
  }

  return {
    term1: {
      required: term1Required,
      paid: term1Paid,
      carryover: broughtForward, // Explicitly show what was brought forward
      totalDue: term1TotalDue,
      balance: term1NetBalance,
      status: getPaymentStatus(term1Paid, term1TotalDue)
    },
    term2: {
      required: term2Required,
      paid: term2Paid,
      carryover: term2Carryover,
      totalDue: term2TotalDue,
      balance: term2NetBalance,
      status: getPaymentStatus(term2Paid, term2TotalDue)
    },
    term3: {
      required: term3Required,
      paid: term3Paid,
      carryover: term3Carryover,
      totalDue: term3TotalDue,
      balance: term3NetBalance,
      status: getPaymentStatus(term3Paid, term3TotalDue)
    },
    totalOutstanding
  };
};

/**
 * Calculates which term fees are applicable based on the admission date.
 * 
 * CRITICAL LOGIC:
 * - **Ongoing Students** (isOngoing = true): NO term fees apply.
 *   Their ONLY debt is the `outstandingBalance` field, which contains their 
 *   consolidated historical debt from before the software was deployed.
 *   Term fees are NOT charged on top of this - that would be double-charging.
 * 
 * - **New Students** (isOngoing = false): Use admission date logic:
 *   - Join Jan-Apr (Term 1): Pay Term 1, 2, 3.
 *   - Join May-Aug (Term 2): Pay Term 2, 3 (Term 1 waived).
 *   - Join Sept-Oct (Term 3): Pay Term 3 (Term 1, 2 waived).
 *   - Join Nov-Dec (Post-Term): Pay 0 (Next year).
 * 
 * @param admissionDate - When the student was registered in the system
 * @param levelFees - Fee structure with term1, term2, term3 amounts
 * @param isOngoing - Whether the student is an ongoing student (existed before the software)
 */
export const getApplicableTermFees = (
  admissionDate: Date | Timestamp | undefined | string, 
  levelFees: any,
  terms: Term[] = [],
  isOngoing: boolean = false,
  isDaySchool: boolean = false,
  feeAssessmentConfig?: any
): TermTotals => {
  // 1. Calculate Base Fees (Sum categories if they exist, else use direct properties)
  let term1 = 0;
  let term2 = 0;
  let term3 = 0;

  if (levelFees.categories && Array.isArray(levelFees.categories)) {
    levelFees.categories.forEach((cat: any) => {
      // If it's a "paidOnce" category and the student is ongoing, we usually skip it
      // as it's assumed to have been paid in a previous year.
      if (cat.paidOnce && isOngoing) return;

      if (isDaySchool) {
        term1 += Number(cat.daySchoolTerm1 ?? cat.term1 ?? 0);
        term2 += Number(cat.daySchoolTerm2 ?? cat.term2 ?? 0);
        term3 += Number(cat.daySchoolTerm3 ?? cat.term3 ?? 0);
      } else {
        term1 += Number(cat.term1 ?? 0);
        term2 += Number(cat.term2 ?? 0);
        term3 += Number(cat.term3 ?? 0);
      }
    });
  } else {
    // Fallback for flat fee structures
    if (isDaySchool) {
      term1 = Number(levelFees.daySchoolTerm1 ?? levelFees.term1 ?? 0);
      term2 = Number(levelFees.daySchoolTerm2 ?? levelFees.term2 ?? 0);
      term3 = Number(levelFees.daySchoolTerm3 ?? levelFees.term3 ?? 0);
    } else {
      term1 = Number(levelFees.term1 ?? 0);
      term2 = Number(levelFees.term2 ?? 0);
      term3 = Number(levelFees.term3 ?? 0);
    }
  }

  const fees = { term1, term2, term3, annual: 0 };

  // ONGOING STUDENTS:
  // They are continuing students, so they are eligible for all term fees of the current academic year.
  // Historical debt from prior years is handled via 'outstandingBalance' separately.
  if (isOngoing) {
    const annual = fees.term1 + fees.term2 + fees.term3;
    let applicable = { ...fees, annual };
    
    // Apply Government Sponsorship Deduction if applicable
    if (levelFees.isGovernmentSponsored && levelFees.governmentSponsorshipAmount) {
      const govAmount = Number(levelFees.governmentSponsorshipAmount);
      let remainingGov = govAmount;
      
      // Deduct from terms sequentially
      if (applicable.term1 > 0) {
        const deduct = Math.min(applicable.term1, remainingGov);
        applicable.term1 -= deduct;
        remainingGov -= deduct;
      }
      if (remainingGov > 0 && applicable.term2 > 0) {
        const deduct = Math.min(applicable.term2, remainingGov);
        applicable.term2 -= deduct;
        remainingGov -= deduct;
      }
      if (remainingGov > 0 && applicable.term3 > 0) {
        const deduct = Math.min(applicable.term3, remainingGov);
        applicable.term3 -= deduct;
        remainingGov -= deduct;
      }
      applicable.annual = applicable.term1 + applicable.term2 + applicable.term3;
    }
    
    return applicable;
  }

  // NEW STUDENTS: Use admission date logic
  const date = normalizeDate(admissionDate);

  if (!date) {
      // If no admission date for new student, return 0 to trigger "Missing Data" warning
      return { term1: 0, term2: 0, term3: 0, annual: 0 };
  }

  // Determine the term context for the admission date
  const startTerm = getCurrentTerm(terms, date);
  
  let applicable = { ...fees };

  if (!startTerm) {
      // Fallback if no term found (e.g., end of year): Assume next year, so 0 for now
      applicable.term1 = 0;
      applicable.term2 = 0;
      applicable.term3 = 0;
  } else {
      // Logic based on the "Start Term"
      if (startTerm.number === 2) {
          applicable.term1 = 0;
      } else if (startTerm.number === 3) {
          applicable.term1 = 0;
          applicable.term2 = 0;
      }
  }

  // Apply Government Sponsorship Deduction if applicable
  if (levelFees.isGovernmentSponsored && levelFees.governmentSponsorshipAmount) {
    const govAmount = Number(levelFees.governmentSponsorshipAmount);
    let remainingGov = govAmount;
    
    if (applicable.term1 > 0) {
      const deduct = Math.min(applicable.term1, remainingGov);
      applicable.term1 -= deduct;
      remainingGov -= deduct;
    }
    if (remainingGov > 0 && applicable.term2 > 0) {
      const deduct = Math.min(applicable.term2, remainingGov);
      applicable.term2 -= deduct;
      remainingGov -= deduct;
    }
    if (remainingGov > 0 && applicable.term3 > 0) {
      const deduct = Math.min(applicable.term3, remainingGov);
      applicable.term3 -= deduct;
      remainingGov -= deduct;
    }
  }

  applicable.annual = applicable.term1 + applicable.term2 + applicable.term3;
  return applicable;
};
